import { Injectable, inject } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { MSAL_SILENT_REDIRECT_URI } from '../../msal-config.factory';
import { SIGNALR_EVENTS } from '../events/signalr-events';
import { environment } from '../../../environments/environment';

interface WorkerStats {
  id: number;
  worker: Worker;
  busy: boolean;
  avgTime: number;
  jobsCompleted: number;
  lastStart?: number;
}

interface WorkerJob {
  id: number;
  event: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
}

@Injectable({
  providedIn: 'root',
})
export class SignalRWorkerService {
  private readonly poolSize = 1;
  private readonly slowThreshold = 1500; // ms
  private readonly maxLifetimeJobs = 100;

  private workers: WorkerStats[] = [];
  private jobCounter = 0;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private eventHandlers: Record<string, (message: any) => void> = {};
  private jobQueue: WorkerJob[] = [];
  private isSignalRInitialized = false;

  private msalService = inject(MsalService);

  constructor() {
    if (typeof Worker === 'undefined') {
      console.error('Web Workers are not supported in this environment.');
      return;
    }

    for (let i = 0; i < this.poolSize; i++) {
      this.createWorker(i + 1);
    }

    if (environment.debugWorker) {
      setInterval(() => {
        console.table(
          this.workers.map((w) => ({
            Worker: `#${w.id}`,
            Busy: w.busy,
            'Jobs Completed': w.jobsCompleted,
            'Avg Time (ms)': w.avgTime.toFixed(1),
          })),
        );
      }, 10000);
    }
  }

  private createWorker(id: number) {
    const worker = new Worker(
      new URL('../../workers/signalR.worker', import.meta.url),
      { type: 'module' },
    );

    const stats: WorkerStats = {
      id,
      worker,
      busy: false,
      avgTime: 0,
      jobsCompleted: 0,
    };

    worker.onmessage = ({ data }) => {
      const start = stats.lastStart ?? performance.now();
      const duration = performance.now() - start;

      stats.jobsCompleted++;
      stats.avgTime =
        (stats.avgTime * (stats.jobsCompleted - 1) + duration) /
        stats.jobsCompleted;

      stats.busy = false;

      if (
        stats.avgTime > this.slowThreshold ||
        stats.jobsCompleted >= this.maxLifetimeJobs
      ) {
        if (environment.debugWorker) {
          console.warn(
            `♻️ Restarting SignalR Worker-${stats.id} (avg=${stats.avgTime.toFixed(
              1,
            )}ms)`,
          );
        }
        this.restartWorker(stats);
        return;
      }

      if (data?.type === 'connectionState') {
        sessionStorage.setItem('signalRConnection', data.state);
      } else if (data?.event && this.eventHandlers[data.event]) {
        this.eventHandlers[data.event](data.data);
      }

      if (this.jobQueue.length > 0) {
        const nextJob = this.jobQueue.shift();
        if (nextJob) this.assignJob(stats, nextJob);
      }
    };

    worker.onerror = (err) => {
      if (environment.debugWorker) {
        console.error(`⚠️ Worker-${stats.id} error`, err);
      }
      stats.busy = false;
    };

    if (environment.debugWorker) {
      console.log(`✅ SignalR Worker-${stats.id} initialized`);
    }
    this.workers.push(stats);
  }

  private restartWorker(stats: WorkerStats) {
    try {
      stats.worker.terminate();
    } catch {
      /* empty */
    }

    const newWorker = new Worker(
      new URL('../../workers/signalR.worker', import.meta.url),
      { type: 'module' },
    );

    newWorker.onmessage = stats.worker.onmessage!;
    newWorker.onerror = stats.worker.onerror!;

    stats.worker = newWorker;
    stats.avgTime = 0;
    stats.jobsCompleted = 0;
    stats.busy = false;

    if (environment.debugWorker) {
      console.log(`✅ Worker-${stats.id} restarted successfully`);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  on(event: string, callback: (message: any) => void) {
    this.eventHandlers[event] = callback;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  listenAll(handlers: Record<string, (message: any) => void>) {
    SIGNALR_EVENTS.forEach((event) => {
      if (handlers[event]) this.on(event, handlers[event]);
    });
  }

  /**
   * Initialize SignalR connection with authentication token
   */
  async initializeWithAuth(): Promise<void> {
    // Prevent multiple initializations
    if (this.isSignalRInitialized) {
      console.log('ℹ️ SignalR already initialized, skipping...');
      return;
    }

    try {
      console.log('🔑 Getting access token...');
      const token = await this.getAccessToken();
      if (token) {
        console.log('📤 Sending token to workers...');
        this.workers.forEach((stats) => {
          stats.worker.postMessage({
            type: 'auth',
            token,
          });
        });
        this.isSignalRInitialized = true;
        console.log('✅ SignalR workers authenticated with token');
      } else {
        console.error('❌ No token received');
      }
    } catch (error) {
      console.error('❌ Error initializing SignalR with auth:', error);
    }
  }

  /**
   * Get access token from MSAL
   */
  private async getAccessToken(): Promise<string> {
    try {
      const account = this.msalService.instance.getActiveAccount();
      if (!account) {
        console.warn('⚠️ No active account found');
        return '';
      }

      const result = await this.msalService.instance.acquireTokenSilent({
        scopes: [environment.mslConfig.apiScopes],
        account,
        redirectUri: MSAL_SILENT_REDIRECT_URI,
      });

      return result.accessToken;
    } catch (error) {
      console.error('Error getting access token:', error);
      return '';
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleSignalREvent(event: string, data: any) {
    const id = ++this.jobCounter;
    const job: WorkerJob = { id, event, data };

    const available = this.getBestIdleWorker();
    if (available) {
      this.assignJob(available, job);
    } else {
      this.jobQueue.push(job);
    }
  }

  private getBestIdleWorker(): WorkerStats | undefined {
    const idle = this.workers.filter((w) => !w.busy);
    if (idle.length === 0) return undefined;

    return idle.reduce((best, next) =>
      next.avgTime < best.avgTime ? next : best,
    );
  }

  private assignJob(workerStats: WorkerStats, job: WorkerJob) {
    workerStats.busy = true;
    workerStats.lastStart = performance.now();

    workerStats.worker.postMessage({
      event: job.event,
      data: job.data,
      id: job.id,
    });
  }

  stop() {
    for (const stats of this.workers) {
      stats.worker.postMessage('stop');
      stats.worker.terminate();
    }
    this.workers = [];
    this.isSignalRInitialized = false;
    this.workers = [];
    this.jobQueue = [];
  }

  restart() {
    for (const stats of this.workers) {
      this.restartWorker(stats);
    }
  }
}
